import { AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ISTPersonActivity } from 'aa-ng';
import { ITableColumn } from 'aa-ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
  tap,
} from 'rxjs';
import { IHiddenFields } from 'src/app/models';

import { IResultSet } from '../../models';
import {
  IActivitySearchFilterCounts,
  IActivitySearchFilters,
  IActivitySearchResult,
} from '../../models/activity-search';
import { IPersonSearchResult } from '../../models/person-search';
import { ActivitySearchService, InstitutionsService } from '../../services';
import { ActivityTableComponent } from '../activity-table/activity-table.component';
import { BaseDirective } from '../base.component';
import { PaginationMenuComponent } from '../pagination-menu/pagination-menu.component';

@Component({
  selector: 'app-relevancy-activities',
  imports: [ActivityTableComponent, AsyncPipe, PaginationMenuComponent],
  templateUrl: './relevancy-activities.component.html',
})
export class RelevancyActivitiesComponent
  extends BaseDirective
  implements OnInit
{
  private route = inject(ActivatedRoute);
  private activitySearchService = inject(ActivitySearchService);
  private institutionsService = inject(InstitutionsService);

  activities$ = new BehaviorSubject<ISTPersonActivity[]>([]);
  totalActivities$ = new BehaviorSubject<number>(0);

  searchWithinResultsQuery$ = new BehaviorSubject<string>('');
  pageNumber$ = new BehaviorSubject<number>(1);
  pageSize$ = new BehaviorSubject<number>(25);
  hiddenFields: IHiddenFields;
  activitySearchFilterCounts$ =
    new BehaviorSubject<IActivitySearchFilterCounts>({});
  filters$ = new BehaviorSubject<IActivitySearchFilters>({});
  _person: IPersonSearchResult;

  @Input() set person(person: IPersonSearchResult) {
    this._person = person;
    this.fetchActivitySearchFilterCounts();
  }

  @Output() loading = new EventEmitter<boolean>();

  columns: ITableColumn[] = [
    {
      name: 'Year',
      property: 'activityYear',
      render: 'year',
    },
    {
      name: 'Works',
      render: 'activity',
      data: { showAbstracts: true, lazyLoadScholars: true },
    },
  ];

  ngOnInit(): void {
    this._subscriptions.push(
      combineLatest([
        this.pageNumber$,
        this.pageSize$,
        this.searchWithinResultsQuery$.pipe(
          debounceTime(750),
          distinctUntilChanged(),
        ),
        this.filters$,
      ])
        .pipe(
          tap(() => this.loading.emit(true)),
          switchMap(([pageNumber, pageSize, searchWithinResults, filters]) => {
            const query = this.route.snapshot.queryParams['query'];
            return this.activitySearchService
              .searchForPersonPaged({
                query: {
                  pageIndex: pageNumber - 1,
                  pageSize: pageSize,
                  queryCriteria: query,
                  sortCriteria: null,
                },
                filters: {
                  personGuids: [this._person.guid],
                  searchWithinResultsQuery: searchWithinResults,
                  activityTypeIds: filters?.activityTypeIds ?? [],
                  years: filters?.years ?? [],
                },
              })
              .pipe(
                debounceTime(100),
                tap((resultSet: IResultSet<IActivitySearchResult[]>) => {
                  // Contributors are deliberately NOT filtered here. This list pages them
                  // server-side, and aa-activity only offers its "Show scholars" control when the
                  // set it was handed is empty - so removing anyone would both hide a real
                  // collaborator and suppress the fetch that would have loaded the full list.
                  const originalActivities = structuredClone(resultSet.results);

                  this.activities$.next(originalActivities);
                  this.totalActivities$.next(resultSet.totalResults);
                }),
                finalize(() => this.loading.emit(false)),
              );
          }),
        )
        .subscribe(),
      this.institutionsService.hiddenFields.subscribe(
        (hf) => (this.hiddenFields = hf),
      ),
    );
  }

  updateSearchQuery(query: string) {
    this.searchWithinResultsQuery$.next(query);
  }

  updateFilters(filters: IActivitySearchFilters) {
    this.filters$.next(filters);
  }

  private fetchActivitySearchFilterCounts() {
    const query = this.route.snapshot.queryParams['query'];
    if (this._person?.guid && query) {
      this._subscriptions.push(
        this.activitySearchService
          .getFilterCounts({
            filters: {
              personGuids: [this._person.guid],
            },
            query: {
              pageIndex: 0,
              pageSize: 0,
              queryCriteria: query,
              sortCriteria: null,
            },
          })
          .pipe(
            tap((filterCounts) => {
              this.activitySearchFilterCounts$.next(filterCounts);
            }),
          )
          .subscribe(),
      );
    }
  }
}
